import { ReactNode, useRef } from "react";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { ChevronRightIcon, PlusIcon } from "@heroicons/react/24/outline";
import { Button } from "../primitives/Button";
import { Sheet } from "../primitives/Sheet";
import { focusRing } from "@ui/tokens/interaction";

export interface AddEntry {
  id: string;
  label: string;
  /** What the entry produces, in the words a teacher would use. */
  hint: string;
  icon: ReactNode;
  /** Drawn but inert — the destination isn't ready yet. */
  disabled?: boolean;
}

interface AddMenuProps {
  entries: readonly AddEntry[];
  onSelect: (id: string) => void;
  /** Mobile turns the menu into a bottom sheet, opened by the shell's own button. */
  compact?: boolean;
  isOpen?: boolean;
  onClose?: () => void;
}

const TILE_BY_ENTRY: Record<string, string> = {
  resource: "bg-lavender-bg text-lavender-ink",
  task: "bg-seen-high-bg text-seen-high-fg",
  event: "bg-seen-mid-bg text-seen-mid-fg",
};

const NEUTRAL_TILE = "bg-tint/[0.08] text-ink-help";

const entryStyles =
  "flex items-center gap-2.75 w-full min-h-touch px-2.5 py-2.25 rounded-slot text-left transition duration-hover";

const EntryBody = ({ entry }: { entry: AddEntry }) => (
  <>
    <span
      className={`flex items-center justify-center shrink-0 w-9 h-9 rounded-subcard [&_svg]:w-4.5 [&_svg]:h-4.5 ${
        entry.disabled ? "bg-tint/[0.06] text-ink-meta-faint" : (TILE_BY_ENTRY[entry.id] ?? NEUTRAL_TILE)
      }`}
    >
      {entry.icon}
    </span>
    <span className="flex-1 min-w-0 flex flex-col gap-px">
      <span
        className={`text-card-title font-700 tracking-tight-sm ${
          entry.disabled ? "text-ink-meta-faint" : "text-ink"
        }`}
      >
        {entry.label}
      </span>
      <span className="text-meta font-500 text-ink-meta">{entry.hint}</span>
    </span>
    {!entry.disabled && (
      <ChevronRightIcon
        className="w-3.5 h-3.5 shrink-0 text-ink-meta opacity-0 transition-opacity duration-hover motion-reduce:transition-none group-data-[focus]:opacity-100"
        strokeWidth={2.2}
        aria-hidden="true"
      />
    )}
  </>
);

/**
 * One door in. The black button of the top bar creates nothing by itself: it names the three
 * things that can be created and says what each produces.
 *
 * Opening on hover is done by clicking the button rather than by holding a second open state —
 * Headless UI then keeps the keyboard, the Escape and the outside click, and hover becomes a
 * shortcut over a menu that works without it.
 */
export const AddMenu = ({
  entries,
  onSelect,
  compact = false,
  isOpen = false,
  onClose,
}: AddMenuProps) => {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const closing = useRef<number>();
  const openedByHover = useRef(false);

  if (compact) {
    return (
      <Sheet isOpen={isOpen} onClose={onClose ?? (() => undefined)} title="Ajouter">
        <div className="flex flex-col gap-0.75 pt-2.5">
          {entries.map((entry) => (
            <button
              key={entry.id}
              type="button"
              onClick={() => {
                onClose?.();
                onSelect(entry.id);
              }}
              disabled={entry.disabled}
              className={`${entryStyles} ${entry.disabled ? "cursor-default" : `hover:bg-row-hover ${focusRing}`}`}
            >
              <EntryBody entry={entry} />
            </button>
          ))}
        </div>
      </Sheet>
    );
  }

  return (
    <Menu>
      {({ open }) => (
        <div
          className="relative shrink-0 flex"
          /*
           * A pointer entering the button opens the menu, so the click that follows would toggle
           * it straight back shut — the menu would look broken to anyone who clicks rather than
           * waits. The first click after a hover-open is swallowed; every later one toggles.
           */
          onClickCapture={(event) => {
            if (openedByHover.current && buttonRef.current?.contains(event.target as Node)) {
              event.preventDefault();
              event.stopPropagation();
              openedByHover.current = false;
            }
          }}
          onMouseEnter={() => {
            window.clearTimeout(closing.current);
            if (!open) {
              // The flag is raised after the click, not before: click() is synchronous, so raising
              // it first would make the capture guard swallow the very click that opens the menu.
              buttonRef.current?.click();
              openedByHover.current = true;
            }
          }}
          onMouseLeave={() => {
            window.clearTimeout(closing.current);
            closing.current = window.setTimeout(() => {
              openedByHover.current = false;
              if (open) buttonRef.current?.click();
            }, 140);
          }}
        >
          {/* The trigger IS the create button, not a copy of its classes. */}
          <MenuButton
            ref={buttonRef}
            as={Button}
            variant="create"
            icon={<PlusIcon className="w-4 h-4 text-[color:var(--add-icon-ink,rgb(var(--signature-deep)))]" strokeWidth={2.6} aria-hidden="true" />}
          >
            Ajouter
          </MenuButton>

          <MenuItems
            transition
            anchor={{ to: "bottom end", gap: 9 }}
            className="z-50 w-add-menu p-2 outline-none rounded-panel bg-add-menu-glass backdrop-blur-xl shadow-add-menu-glass origin-top-right transition duration-hover motion-reduce:transition-none data-[closed]:opacity-0 data-[closed]:scale-95"
          >
            {entries.map((entry) => (
              <MenuItem key={entry.id} disabled={entry.disabled}>
                <button
                  type="button"
                  onClick={() => onSelect(entry.id)}
                  disabled={entry.disabled}
                  className={`${entryStyles} ${entry.disabled ? "cursor-default" : "group data-[focus]:bg-ink/[0.07]"}`}
                >
                  <EntryBody entry={entry} />
                </button>
              </MenuItem>
            ))}
          </MenuItems>
        </div>
      )}
    </Menu>
  );
};
